import { createFileRoute, useSearch } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { BillingInfoCard } from "@/components/settings/billing-info-card";
import { DangerZoneCard } from "@/components/settings/danger-zone-card";
import { InvoicesCard } from "@/components/settings/invoices-card";
import { SeriesSelector } from "@/components/settings/series-selector";
import { SubscriptionCard } from "@/components/settings/subscription-card";
import { useProfile } from "@/hooks/use-profile";
import { useSubscription } from "@/hooks/use-subscription";

export const Route = createFileRoute("/_auth/app/settings/")({
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    checkout: (search.checkout as string) ?? undefined,
  }),
});

function SettingsPage() {
  const { checkout } = useSearch({ from: "/_auth/app/settings/" });
  const { data: profile } = useProfile();
  const { data: subscription } = useSubscription(profile?.id);
  const [showSuccess, setShowSuccess] = useState(false);
  const seriesSelectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (checkout === "success") {
      setShowSuccess(true);
      // Clear the search param from URL without navigating
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      window.history.replaceState({}, "", url.toString());
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [checkout]);

  // Auto-scroll to series selector for new Plus subscribers after checkout
  const needsSeriesSelection =
    subscription?.tier === "plus" &&
    (!subscription.selectedSeriesIds || subscription.selectedSeriesIds.length === 0);

  useEffect(() => {
    if (showSuccess && needsSeriesSelection && seriesSelectorRef.current) {
      // Small delay to let the DOM settle
      const timer = setTimeout(() => {
        seriesSelectorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, needsSeriesSelection]);

  const tier = subscription?.tier ?? "free";

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-[Bebas_Neue] text-3xl text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your subscription and preferences</p>
      </div>

      {/* Checkout success banner */}
      {showSuccess && (
        <div className="flex items-center gap-3 rounded-xl border border-folk-green/30 bg-folk-green/10 px-4 py-3">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-folk-green" />
          <div>
            <p className="text-sm font-medium text-foreground">Subscription activated!</p>
            <p className="text-xs text-muted-foreground">
              Thank you for subscribing. Your new plan is now active.
            </p>
          </div>
          <button
            onClick={() => setShowSuccess(false)}
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            &times;
          </button>
        </div>
      )}

      {/* Subscription card */}
      <SubscriptionCard />

      {/* Series selector — only for Plus subscribers */}
      {tier === "plus" && subscription && profile?.id && (
        <div
          ref={seriesSelectorRef}
          className={
            needsSeriesSelection
              ? "rounded-xl ring-2 ring-folk-blue ring-offset-2 ring-offset-background"
              : ""
          }
        >
          <SeriesSelector subscription={subscription} userId={profile.id} />
        </div>
      )}

      {/* Billing info — only for paid subscribers */}
      {tier !== "free" && <BillingInfoCard />}

      {/* Invoices — only for paid subscribers */}
      {tier !== "free" && <InvoicesCard />}

      {/* Danger zone — all users */}
      <DangerZoneCard />
    </div>
  );
}
